import React from 'react'
import {
    Box,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import {
    CommunityDayHours,
    CommunityOfficeHours,
    CommunityWeekday,
} from '../../../../models/Company/types'
import { COMMUNITY_WEEKDAYS } from '../../../../hooks/useCommunityProfile'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.25),
    },
    caption: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        lineHeight: 1.45,
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: '0.95rem',
    },
    row: {
        display: 'grid',
        gridTemplateColumns: '120px 140px 1fr 1fr',
        gap: theme.spacing(1.25),
        alignItems: 'center',
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    dayName: {
        fontWeight: 600,
        fontSize: '0.875rem',
    },
}))

type Props = {
    value: CommunityOfficeHours
    onChange: (hours: CommunityOfficeHours) => void
}

export const CommunityHoursSection: React.FC<Props> = ({ value, onChange }) => {
    const classes = useStyles()

    const setDay = (
        day: CommunityWeekday,
        patch: Partial<CommunityDayHours>,
    ) => {
        onChange({
            ...value,
            [day]: {
                ...value[day],
                ...patch,
            },
        })
    }

    return (
        <Box className={classes.root}>
            <Box>
                <Typography className={classes.sectionTitle}>
                    Office hours
                </Typography>
                <Typography className={classes.caption}>
                    Used by the voice agent and tour booking.
                </Typography>
            </Box>
            {COMMUNITY_WEEKDAYS.map((day) => {
                const row = value[day]
                const label = day.charAt(0).toUpperCase() + day.slice(1)
                return (
                    <Box key={day} className={classes.row}>
                        <Typography className={classes.dayName}>
                            {label}
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={!row.closed}
                                    onChange={(event) =>
                                        setDay(day, {
                                            closed: !event.target.checked,
                                            start: event.target.checked
                                                ? row.start
                                                : '',
                                            end: event.target.checked
                                                ? row.end
                                                : '',
                                        })
                                    }
                                />
                            }
                            label={row.closed ? 'Closed' : 'Open'}
                        />
                        <TextField
                            label="Opens"
                            type="time"
                            size="small"
                            variant="outlined"
                            value={row.start}
                            onChange={(event) =>
                                setDay(day, { start: event.target.value })
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            disabled={row.closed}
                        />
                        <TextField
                            label="Closes"
                            type="time"
                            size="small"
                            variant="outlined"
                            value={row.end}
                            onChange={(event) =>
                                setDay(day, { end: event.target.value })
                            }
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ step: 300 }}
                            disabled={row.closed}
                        />
                    </Box>
                )
            })}
        </Box>
    )
}
